import { storeToRefs } from 'pinia';
import { computed, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { useMutation, useSubscription } from '@vue/apollo-composable';

import {
  ChatMessageType,
  ChatStatus,
  ChatUpdatedDocument,
  SetMyChatNotificationPrefsDocument,
} from '@ugi/graphql';
import type { ChatUpdatedSubscription } from '@ugi/graphql';

import { useWindowAttention } from '@/shared/composables/useWindowAttention';
import { isGuestChat, resolveChatCounterpartName } from '@/shared/tools/chat-counterpart';
import { useAuthStore } from '@/stores/auth';

const MIN_INTERVAL_BETWEEN_SOUNDS_MS = 2_000;

/**
 * Наскільки свіжою має бути подія, щоб перша ж побачена зміна чату про неї
 * сповістила (див. `baselineForFirstSighting`). Запас на розбіжність годинників
 * сервера й браузера та на затримку доставки через WS.
 */
const FIRST_SIGHTING_FRESH_MS = 2 * 60_000;

/**
 * Подія для заголовка вкладки (`useAppBadge`): прийшло те, на що секретар має
 * відреагувати, а він на вікно не дивиться. Окрема подія, а не спільний стан,
 * — так само, як `ugi:chat-updated` нижче: підписка одна, слухачів скільки треба.
 */
export const CHAT_ALERT_EVENT = 'ugi:chat-alert';

type ChatPayload = ChatUpdatedSubscription['chatUpdated'];

interface SeenState {
  status: ChatStatus;
  lastMessageId: string | null;
}

class ChatChime {
  private context: AudioContext | null = null;
  private unlockBound = false;
  private isReady = false;

  ensureUnlockListener(): void {
    if (this.unlockBound || typeof window === 'undefined') {
      return;
    }

    this.unlockBound = true;
    const onGesture = () => {
      void this.unlock();
    };

    window.addEventListener('pointerdown', onGesture, { once: false, passive: true });
    window.addEventListener('keydown', onGesture, { once: false, passive: true });
  }

  private async unlock(): Promise<void> {
    try {
      if (!this.context) {
        const Ctor = window.AudioContext
          ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

        if (!Ctor) {
          return;
        }
        this.context = new Ctor();
      }

      if (this.context.state === 'suspended') {
        await this.context.resume();
      }

      this.isReady = this.context.state === 'running';
    } catch {
      this.isReady = false;
    }
  }

  play(): void {
    if (!this.isReady || !this.context) {
      return;
    }

    const ctx = this.context;
    const now = ctx.currentTime;

    this.tone(ctx, now, 523.25, 0.18);
    this.tone(ctx, now + 0.12, 659.25, 0.22);
  }

  private tone(ctx: AudioContext, startAt: number, frequency: number, durationSec: number): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(frequency, startAt);

    gain.gain.setValueAtTime(0, startAt);
    gain.gain.linearRampToValueAtTime(0.18, startAt + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + durationSec);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(startAt);
    osc.stop(startAt + durationSec + 0.05);
  }
}

const chime = new ChatChime();

export const useChatSoundEnabled = () => {
  const authStore = useAuthStore();
  const { user } = storeToRefs(authStore);

  const enabled = computed<boolean>(() => Boolean(user.value?.soundNotificationsChatEnabled));

  const { mutate: persist, loading } = useMutation(SetMyChatNotificationPrefsDocument);

  const set = async (value: boolean): Promise<void> => {
    await persist({ variables: { input: { soundEnabled: value } } });
    await authStore.fetchMe();
  };

  return {
    enabled,
    saving: loading,
    set,
  };
};

const showDesktopNotification = (title: string, body: string, tag?: string): void => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return;
  }

  if (Notification.permission !== 'granted') {
    return;
  }

  try {
    const notification = new Notification(title, {
      body,
      icon: '/favicon.ico',
      tag,
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch {
    // Silently swallow — desktop notification is best-effort.
  }
};

const isFresh = (iso: string | null | undefined, now: number): boolean => {
  if (!iso) {
    return false;
  }

  return now - new Date(iso).getTime() <= FIRST_SIGHTING_FRESH_MS;
};

/**
 * «Попередній стан» для чату, який ця вкладка бачить уперше.
 *
 * Підписка — це лише потік змін, без знімка того, що було до неї: після
 * перезавантаження сторінки про жоден чат ще нічого не відомо, і перша ж
 * CHAT_UPDATED приходить без `prev`. Раніше таку подію просто запам'ятовували й
 * мовчали — тож перше повідомлення студента в моєму чаті після перезавантаження
 * не давало ні звуку, ні сповіщення, ні сигналу в заголовку.
 *
 * Окремого запиту на знімок не робимо: подія вже несе час. Якщо останнє
 * повідомлення чи початок поточної ескалації свіжі — це і є те, через що подія
 * прийшла, і `prev` будуємо так, наче до неї їх не було. Старі — `prev`
 * дорівнює поточному стану, і сповіщати нема про що (CHAT_UPDATED від
 * прочитання, передачі тощо на давно відомому чаті). Ціна — у вікні
 * `FIRST_SIGHTING_FRESH_MS` після перезавантаження будь-яка інша зміна того ж
 * чату може повторити вже показане сповіщення; мовчання дорожче.
 */
const baselineForFirstSighting = (chat: ChatPayload, now: number): SeenState => {
  const escalationIsFresh = chat.status === ChatStatus.ESCALATED
    && isFresh(chat.currentEscalationStartedAt, now);
  const lastMessageIsFresh = isFresh(chat.lastMessage?.createdAt, now);

  return {
    status: escalationIsFresh ? ChatStatus.IN_PROGRESS : chat.status,
    lastMessageId: lastMessageIsFresh ? null : chat.lastMessage?.id ?? null,
  };
};

export const useChatSoundNotifications = () => {
  const authStore = useAuthStore();
  const { user } = storeToRefs(authStore);
  const route = useRoute();
  const toast = useToast();
  const { isAttending } = useWindowAttention();

  const seen = new Map<string, SeenState>();
  let lastPlayedAt = 0;

  chime.ensureUnlockListener();

  const currentlyViewedChatId = computed<string | null>(() => {
    if (route.name !== 'ChatConversation') {
      return null;
    }

    const param = route.params.chatId;

    return typeof param === 'string' ? param : null;
  });

  const tryPlay = () => {
    if (!user.value?.soundNotificationsChatEnabled) {
      return;
    }

    const now = Date.now();
    if (now - lastPlayedAt < MIN_INTERVAL_BETWEEN_SOUNDS_MS) {
      return;
    }

    lastPlayedAt = now;
    chime.play();
  };

  const detectEvent = (
    next: ChatPayload,
    prev: SeenState | undefined,
  ): 'new-escalation' | 'new-student-message' | null => {
    if (next.outsideBusinessHours) {
      return null;
    }

    // Мовчимо про відкритий чат лише тоді, коли на нього справді дивляться.
    // Раніше вистачало самого маршруту — і секретар, який перемкнувся в іншу
    // програму з відкритим чатом, не дізнавався про нове повідомлення взагалі:
    // ні звуку, ні сповіщення, ні бейджа.
    if (currentlyViewedChatId.value === next.id && isAttending.value) {
      return null;
    }

    const myId = user.value?.id;
    const myChat = !!myId && next.assignedToUser?.id === myId;

    if (next.status === ChatStatus.ESCALATED && (!prev || prev.status !== ChatStatus.ESCALATED)) {
      return 'new-escalation';
    }

    const lastMsg = next.lastMessage ?? null;
    const lastMessageId = lastMsg?.id ?? null;
    const lastMessageType = lastMsg?.type ?? null;
    const lastMessageChanged = lastMessageId !== null && lastMessageId !== (prev?.lastMessageId ?? null);

    if (
      lastMessageChanged
      && lastMessageType === ChatMessageType.STUDENT
      && next.status === ChatStatus.IN_PROGRESS
      && myChat
    ) {
      return 'new-student-message';
    }

    return null;
  };

  const announce = (event: 'new-escalation' | 'new-student-message', chat: ChatPayload) => {
    // Гість — без картки: його називає Telegram-контакт, а не `student`.
    const studentName = resolveChatCounterpartName(chat);
    // Same tag the API puts on the background push (see `chatTag` in
    // chat-push-notification.sender.ts) so the foreground and background
    // notification for one chat collapse into a single toast.
    const tag = `/chats/${chat.id}`;

    if (!isAttending.value) {
      window.dispatchEvent(new CustomEvent(CHAT_ALERT_EVENT));
    }

    if (event === 'new-escalation') {
      tryPlay();
      showDesktopNotification('Нова ескалація', studentName, tag);
      toast.add({
        title: 'Нова ескалація',
        description: studentName,
        color: 'warning',
      });

      return;
    }

    const preview = chat.lastMessage?.content
      ?? (isGuestChat(chat) ? 'Людина написала повідомлення' : 'Студент написав повідомлення');

    tryPlay();
    showDesktopNotification(`Нове повідомлення — ${studentName}`, preview, tag);
    toast.add({
      title: `Нове повідомлення — ${studentName}`,
      description: preview,
      color: 'info',
    });
  };

  const { onResult, stop } = useSubscription(
    ChatUpdatedDocument,
    () => ({ enabled: !!user.value }),
  );

  onResult((data) => {
    const chat = data?.chatUpdated;
    if (!chat) {
      return;
    }

    // Single ChatUpdated subscription lives here (App.vue); other consumers
    // (e.g. the taskbar badge counter in MainLayout) listen to this bridge
    // event instead of opening a duplicate WS subscription.
    window.dispatchEvent(new CustomEvent('ugi:chat-updated'));

    const prev = seen.get(chat.id) ?? baselineForFirstSighting(chat, Date.now());

    seen.set(chat.id, {
      status: chat.status,
      lastMessageId: chat.lastMessage?.id ?? null,
    });

    const event = detectEvent(chat, prev);
    if (event) {
      announce(event, chat);
    }
  });

  onUnmounted(() => {
    stop();
    seen.clear();
  });
};
